import { ReceiptText } from "lucide-react";

import type { WalletTransactionEntity } from "@/modules/dashboard/cards/types/wallets-api.types";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import useTranslations from "@/shared/lang/useTranslations";

import {
  type CardWalletMovementsListProps,
  movementShortDate,
  movementTime,
} from "./cardWalletMovementsList.types";
import { MovementAmount, MovementAuthorCell, MovementDescription, MovementKindBadges } from "./MovementCells";

/**
 * The desktop face, from `xl` up.
 *
 * `minWidth` is the sum of the declared columns: below it the table scrolls horizontally instead of
 * compressing silently. The switch is at `xl` rather than `lg` because 1000px of table needs roughly 1272px
 * of viewport once the dashboard sidebar and the page padding are paid.
 */
export function CardWalletMovementsTable({
  movements,
  isLoading,
  currentUserId,
  highlightedProviderTxId,
}: CardWalletMovementsListProps) {
  const { t, activeLanguage } = useTranslations("cardWallets");
  const locale = activeLanguage || "es";

  const columns: ColocaDataTableColumn<WalletTransactionEntity>[] = [
    {
      id: "date",
      header: t("col_date"),
      headerClassName: "w-[150px]",
      cellClassName: "w-[150px]",
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="tabular-nums">{movementShortDate(row.created_at, locale)}</span>
          <span className="text-xs tabular-nums text-muted-foreground">
            {movementTime(row.created_at, locale)}
          </span>
        </div>
      ),
    },
    {
      id: "movement",
      header: t("col_movement"),
      cellClassName: "max-w-80",
      cell: (row) => (
        <div className="flex min-w-0 flex-col gap-1">
          <MovementKindBadges movement={row} />
          <MovementDescription movement={row} />
        </div>
      ),
    },
    {
      id: "reference",
      header: t("col_reference"),
      headerClassName: "w-[200px]",
      cellClassName: "w-[200px] max-w-44",
      cell: (row) => {
        const reference = row.metadata?.external_reference;
        const text = typeof reference === "string" ? reference : row.provider_transaction_id;
        return (
          <span className="block max-w-44 truncate font-mono text-xs" title={text}>
            {text}
          </span>
        );
      },
    },
    {
      id: "by",
      header: t("col_by"),
      headerClassName: "w-[180px]",
      cellClassName: "w-[180px] max-w-44 text-sm",
      cell: (row) => <MovementAuthorCell movement={row} currentUserId={currentUserId} />,
    },
    {
      id: "amount",
      header: t("col_amount"),
      headerClassName: "w-[150px] text-right",
      cellClassName: "w-[150px] text-right",
      cell: (row) => (
        <span className="flex justify-end">
          <MovementAmount movement={row} />
        </span>
      ),
    },
  ];

  return (
    <ColocaDataTable
      frame="none"
      minWidth={1000}
      columns={columns}
      rows={movements}
      rowKey={(row) => row.id}
      loading={isLoading}
      skeletonRows={6}
      rowClassName={(row) =>
        highlightedProviderTxId && row.provider_transaction_id === highlightedProviderTxId
          ? "bg-primary/5"
          : undefined
      }
      empty={
        <ColocaEmpty
          surface="card"
          icon={ReceiptText}
          title={t("empty_title")}
          description={t("empty_description")}
        />
      }
    />
  );
}
